import { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { getData, insertData, updateData } from "../api";

import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Modal,
} from "@mui/material";

export default function EvaluationExercise() {
  const [openModal, setOpenModal] = useState(false);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [exercise, setExercise] = useState<any>(null);
  const [players, setPlayers] = useState<any[]>([]);
  const [evaluatedPlayers, setEvaluatedPlayers] = useState<{ [key: number]: number }>({});
  const [sessionName, setSessionName] = useState<string>("");

  const [selectedPlayer, setSelectedPlayer] = useState<number | null>(null);
  const [scores, setScores] = useState<number[]>([]);
  const [existingEvaluation, setExistingEvaluation] = useState<any>(null);

  const [teams, setTeams] = useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);

  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSession, setSelectedSession] = useState<number | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  // Charger la liste des équipes
  useEffect(() => {
    getData("teams").then((res: any) => {
      setTeams(res.data || []);

      const storedTeam = localStorage.getItem("selectedTeam");
      if (storedTeam) setSelectedTeam(Number(storedTeam));
    });
  }, []);

  // Charger sessions selon équipe
  useEffect(() => {
    if (selectedTeam) {
      getData("sessions", {
        where: `team = ${selectedTeam}`,
        order_by: "start_date DESC",
      }).then((res: any) => {
        setSessions(res.data || []);
        if (res.data && res.data.length > 0) {
          setSelectedSession(res.data[0].id);
        } else {
          setSelectedSession(null);
        }
      });
    } else {
      setSessions([]);
      setSelectedSession(null);
    }
  }, [selectedTeam]);

  // Charger données principales
  useEffect(() => {
    if (!selectedSession) return;

    // Exercice
    getData("exercises", { where: `id = ${id}` }).then((res: any) => {
      const ex = res.data?.[0];
      setExercise(ex);
      if (ex) setScores(new Array(ex.repetitions).fill(-1));
    });

    // Joueurs
    if (selectedTeam) {
      getData("users", { where: "role = 'player'" }).then(async (res: any) => {
        const users = res.data || [];

        const playersWithProfiles = await Promise.all(
          users.map(async (user: any) => {
            const profileRes = await getData("profiles", {
              where: `user_id = '${user.id}'`,
            });
            const profile = profileRes.data?.[0] || {};
            return { ...user, nom_complet: profile.nom_complet, equipe: profile.equipe };
          })
        );

        setPlayers(
          playersWithProfiles.filter((p: any) => {
            const equipeIds = (p.equipe || "").split(",").map((i: string) => i.trim());
            return equipeIds.includes(String(selectedTeam));
          })
        );
      });
    }

    // Nom session
    getData("sessions", { where: `id = ${selectedSession}` }).then((res: any) => {
      setSessionName(res.data?.[0]?.name || "Session");
    });

    // Liste évaluations pour afficher en vert les joueurs déjà évalués
    getData("evaluations", {
      where: `exercise_id = ${id} AND session_id = ${selectedSession}`,
    }).then((res: any) => {
      const evaluated = res.data?.reduce((acc: any, item: any) => {
        acc[item.player_id] = item.session_id;
        return acc;
      }, {});
      setEvaluatedPlayers(evaluated || {});
    });

  }, [id, selectedSession, selectedTeam, location.key]);

  // Sélection joueur → Charger éventuelle évaluation existante
  const handleSelectPlayer = async (playerId: number) => {
    setSelectedPlayer(playerId);

    const res = await getData("evaluations", {
      where: `exercise_id = ${id} AND session_id = ${selectedSession} AND player_id = ${playerId}`,
    });

    if (res.data && res.data.length > 0) {
      const evalItem = res.data[0];
      setExistingEvaluation(evalItem);
      setScores(JSON.parse(evalItem.details));
    } else {
      setExistingEvaluation(null);
      if (exercise) setScores(new Array(exercise.repetitions).fill(-1));
    }
  };

  const handleScoreClick = (repIndex: number, scoreValue: number) => {
    const updated = [...scores];
    updated[repIndex] = scoreValue;
    setScores(updated);
  };

  // SAVE : INSERT ou UPDATE selon existingEvaluation
  const handleSave = async () => {
    if (!selectedPlayer || !exercise) return;

    setIsSaving(true);

    const payload: any = {
      player_id: selectedPlayer,
      exercise_id: id!,
      score: scores.reduce((a, b) => a + (b > 0 ? b : 0), 0),
      details: JSON.stringify(scores),
      session_id: selectedSession!,
    };

    if (existingEvaluation) {
      // ---------------- UPDATE ----------------
      await updateData("evaluations", payload, `id = ${existingEvaluation.id}`);
    } else {
      // ---------------- INSERT ----------------
      await insertData("evaluations", payload);
    }

    // Rafraîchir la liste des évaluations
    const refresh = await getData("evaluations", {
      where: `exercise_id = ${id} AND session_id = ${selectedSession}`,
    });

    const evaluated = refresh.data?.reduce((acc: any, item: any) => {
      acc[item.player_id] = item.session_id;
      return acc;
    }, {});
    setEvaluatedPlayers(evaluated || {});

    setOpenModal(true);

    setTimeout(() => {
      setOpenModal(false);
      if (exercise) setScores(new Array(exercise.repetitions).fill(-1));
      setSelectedPlayer(null);
      setExistingEvaluation(null);
      setIsSaving(false);
    }, 1000);
  };

  const hasUnrated = scores.some((s) => s === -1);

  return (
    <Box sx={{ padding: 2 }}>
      {/* ---------------- HEADER ---------------- */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          mb: 3,
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <Button variant="outlined" color="primary" onClick={() => navigate("/evaluation")}>
          ← Retour aux exercices
        </Button>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Équipe</InputLabel>
          <Select
            value={selectedTeam || ""}
            label="Équipe"
            onChange={(e) => setSelectedTeam(Number(e.target.value))}
          >
            <MenuItem value="">
              <em>Choisir une équipe</em>
            </MenuItem>
            {teams.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Session</InputLabel>
          <Select
            value={selectedSession || ""}
            label="Session"
            onChange={(e) => setSelectedSession(Number(e.target.value))}
            disabled={!selectedTeam}
          >
            {sessions.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name} ({s.start_date})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!selectedTeam && <Typography>Veuillez sélectionner une équipe.</Typography>}
      {selectedTeam && !selectedSession && (
        <Typography>Veuillez sélectionner une session.</Typography>
      )}

      {/* ---------------- CONTENU ---------------- */}
      {selectedTeam && selectedSession && exercise && (
        <>
          <Typography variant="h1" sx={{ mb: 2 }}>
            {exercise.title}
          </Typography>

          <Typography sx={{ mb: 2 }}>{exercise.description}</Typography>
          <Typography sx={{ mb: 4 }}>
            Points max par tentative : {exercise.max_points}
          </Typography>

          {/* -------- LISTE JOUEURS -------- */}
          {!selectedPlayer && (
            <>
              <Typography variant="h2" sx={{ mb: 2 }}>
                Choisir un(e) joueur(se) :
              </Typography>

              {players.map((p) => (
                <Button
                  key={p.id}
                  onClick={() => handleSelectPlayer(p.id)}
                  variant="contained"
                  sx={{
                    mr: 2,
                    mb: 2,
                    backgroundColor: evaluatedPlayers[p.id]
                      ? "success.main"
                      : "secondary.main",
                    color: "white",
                  }}
                >
                  {p.nom_complet}
                  {evaluatedPlayers[p.id] && ` (${sessionName})`}
                </Button>
              ))}
            </>
          )}

          {/* -------- EVALUATION -------- */}
          {selectedPlayer && (
            <>
              <Typography variant="h2" sx={{ mb: 2 }}>
                Évaluer {players.find((p) => p.id === selectedPlayer)?.nom_complet}
              </Typography>

              {existingEvaluation && (
                <Typography sx={{ mb: 2, color: "orange" }}>
                  ⚠️ Modification d’une évaluation existante
                </Typography>
              )}

              {scores.map((score, i) => (
                <Box key={i} sx={{ mb: 3 }}>
                  <Typography>Tentative {i + 1} :</Typography>

                  <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                    {/* NON ÉVALUÉ */}
                    <Button
                      onClick={() => handleScoreClick(i, -1)}
                      variant={score === -1 ? "contained" : "outlined"}
                      color={score === -1 ? "warning" : "primary"}
                    >
                      Non évalué
                    </Button>

                    {/* SCORES */}
                    {[...Array(exercise.max_points + 1)].map((_, j) => (
                      <Button
                        key={j}
                        onClick={() => handleScoreClick(i, j)}
                        variant={score === j ? "contained" : "outlined"}
                        color={score === j ? "success" : "primary"}
                      >
                        {j}
                      </Button>
                    ))}
                  </Box>
                </Box>
              ))}

              {/* -------- BOUTON SAVE -------- */}
              <Button
                onClick={handleSave}
                variant="contained"
                color="primary"
                disabled={hasUnrated || isSaving}
                sx={{
                  mt: 4,
                  padding: "12px 24px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  backgroundColor:
                    hasUnrated || isSaving ? "grey.400" : "primary.main",
                }}
              >
                {isSaving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </>
          )}
        </>
      )}

      {/* ---------------- MODAL ---------------- */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            p: 4,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            minWidth: 300,
            textAlign: "center",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: "success.main" }}>
            Évaluation enregistrée !
          </Typography>
          <Typography color="text.secondary">
            Retour à la sélection du joueur…
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
}
